import { Box, Button, MenuItem, Stack, TextField } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ConfirmDialog,
  EmptyState,
  InlineAlert,
  LoadingButton,
  PagedDataTable,
  SearchField,
  StatusChip,
} from "src/grading/components";
import type { DataTableColumn } from "src/grading/components/DataTable/DataTable";
import {
  messageOf,
  refusalCode,
  refusalCopy,
} from "src/grading/utils/apiError";
import { permissions } from "src/grading/utils/permissionsHelpers";
import { InvitationStatus, InvitationSummary } from "src/models/staff";
import {
  useListInvitationsQuery,
  useResendInvitationMutation,
  useRevokeInvitationMutation,
} from "src/store/reducers/invitations/invitationsApiSlice";
import InviteStaffDialog from "./InviteStaffDialog";
import usePeopleScope from "./usePeopleScope";
import { usePeopleUrlState } from "./usePeopleUrlState";

const STATUSES: InvitationStatus[] = [
  "pending",
  "accepted",
  "expired",
  "revoked",
];
/** The URL's `status` when every status is listed; no `status` means the default, pending. */
const ALL = "all";

export interface InvitationsTabProps {
  /** The platform admin's district name, shown locked in the invite dialog. */
  districtName?: string;
}

type Notice = { severity: "success" | "error"; text: string };

/** Invitations in the viewer's scope, pending by default, with Resend and Revoke. */
export const InvitationsTab = ({ districtName }: InvitationsTabProps) => {
  const { t, i18n } = useTranslation();
  const url = usePeopleUrlState();
  const scope = usePeopleScope(url.district);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [revoking, setRevoking] = useState<InvitationSummary | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);

  const statusValue =
    url.status === ALL
      ? ALL
      : (STATUSES.find((status) => status === url.status) ?? "pending");
  const { data, isLoading, isFetching, isError, refetch } =
    useListInvitationsQuery({
      districtId: scope.requestDistrictId,
      status: statusValue === ALL ? undefined : statusValue,
      search: url.q || undefined,
      limit: rowsPerPage,
      offset: url.page * rowsPerPage,
    });
  const [resendInvitation, { isLoading: resending }] =
    useResendInvitationMutation();
  const [revokeInvitation, { isLoading: revokingNow }] =
    useRevokeInvitationMutation();

  const canUpdate = scope.has(permissions.UPDATE_INVITATIONS);
  const canInvite =
    scope.has(permissions.INVITE_STAFF) && scope.grantableRoles.length > 0;
  const rows = data?.items ?? [];
  const filtered = Boolean(url.q || url.status);
  const dateText = (value?: string | null) =>
    value ? new Date(value).toLocaleDateString(i18n.language) : "—";

  const errorText = (error: unknown) => {
    const code = refusalCode(error);
    return code
      ? refusalCopy(t, code)
      : messageOf(error, t("invite.list.actionError"));
  };

  const resend = async (invitation: InvitationSummary) => {
    setNotice(null);
    try {
      await resendInvitation(invitation.id).unwrap();
      setNotice({
        severity: "success",
        text: t("invite.list.resent", { email: invitation.email }),
      });
    } catch (error) {
      setNotice({ severity: "error", text: errorText(error) });
    }
  };

  const revoke = async () => {
    if (!revoking) return;
    setNotice(null);
    try {
      await revokeInvitation(revoking.id).unwrap();
      setNotice({
        severity: "success",
        text: t("invite.list.revoked", { email: revoking.email }),
      });
    } catch (error) {
      setNotice({ severity: "error", text: errorText(error) });
    }
    setRevoking(null);
  };

  const inviteButton = canInvite ? (
    <Button variant="contained" onClick={() => setInviteOpen(true)}>
      {t("staff.invite")}
    </Button>
  ) : null;

  const columns: DataTableColumn<InvitationSummary>[] = [
    {
      key: "email",
      header: t("invite.list.columns.email"),
      render: (invitation) => (
        <Box component="span" sx={{ fontWeight: 700 }}>
          {invitation.email}
        </Box>
      ),
    },
    {
      key: "role",
      header: t("invite.list.columns.role"),
      render: (invitation) => t(`roles.${invitation.role}`),
    },
    {
      key: "organization",
      header: t("invite.list.columns.organization"),
      render: (invitation) => invitation.organization?.name ?? "—",
    },
    {
      key: "classes",
      header: t("invite.list.columns.classes"),
      render: (invitation) =>
        invitation.classes.length
          ? invitation.classes.map((item) => item.name).join(", ")
          : "—",
    },
    {
      key: "status",
      header: t("invite.list.columns.status"),
      render: (invitation) => <StatusChip status={invitation.status} />,
    },
    {
      key: "invitedBy",
      header: t("invite.list.columns.invitedBy"),
      render: (invitation) => invitation.invitedBy?.name ?? "—",
    },
    {
      key: "expires",
      header: t("invite.list.columns.expires"),
      render: (invitation) => dateText(invitation.expiresAt),
    },
    ...(canUpdate
      ? [
          {
            key: "actions",
            header: t("invite.list.columns.actions"),
            align: "right" as const,
            render: (invitation: InvitationSummary) => (
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                {invitation.status === "pending" ||
                invitation.status === "expired" ? (
                  <LoadingButton
                    size="small"
                    variant="outlined"
                    disabled={resending}
                    onClick={() => resend(invitation)}
                    label={t("invite.list.resend")}
                    aria-label={t("invite.list.resendLabel", {
                      email: invitation.email,
                    })}
                  />
                ) : null}
                {invitation.status === "pending" ? (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => setRevoking(invitation)}
                    aria-label={t("invite.list.revokeLabel", {
                      email: invitation.email,
                    })}
                  >
                    {t("invite.list.revoke")}
                  </Button>
                ) : null}
              </Stack>
            ),
          },
        ]
      : []),
  ];

  const emptyState = filtered ? (
    <EmptyState
      headingLevel={3}
      title={t("people.noMatchTitle")}
      body={t("people.noMatchBody")}
      action={
        <Button variant="outlined" onClick={url.clearFilters}>
          {t("people.clearFilters")}
        </Button>
      }
    />
  ) : (
    <EmptyState
      headingLevel={3}
      title={t("invite.list.emptyTitle")}
      body={t("invite.list.emptyBody")}
      action={inviteButton}
    />
  );

  return (
    <Box>
      <Stack
        direction="row"
        flexWrap="wrap"
        useFlexGap
        spacing={1.5}
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <SearchField
          label={t("invite.list.searchLabel")}
          value={url.q}
          onChange={(q) => url.setFilters({ q })}
          sx={{ width: { xs: "100%", sm: 280 } }}
        />
        <TextField
          select
          size="small"
          label={t("invite.list.status")}
          value={statusValue}
          onChange={(event) =>
            url.setFilters({
              status:
                event.target.value === "pending" ? null : event.target.value,
            })
          }
          sx={{ minWidth: 160 }}
        >
          <MenuItem value={ALL}>{t("invite.list.allStatuses")}</MenuItem>
          {STATUSES.map((status) => (
            <MenuItem key={status} value={status}>
              {t(`invite.list.statusOption.${status}`)}
            </MenuItem>
          ))}
        </TextField>
        {inviteButton ? (
          <Box sx={{ ml: { sm: "auto" } }}>{inviteButton}</Box>
        ) : null}
      </Stack>

      {notice ? (
        <Box sx={{ mb: 1.5 }}>
          <InlineAlert severity={notice.severity}>{notice.text}</InlineAlert>
        </Box>
      ) : null}

      {isError ? (
        <InlineAlert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              {t("common.retry")}
            </Button>
          }
        >
          {t("invite.list.loadError")}
        </InlineAlert>
      ) : (
        <PagedDataTable<InvitationSummary>
          aria-label={t("invite.list.tableLabel")}
          columns={columns}
          rows={rows}
          getRowKey={(invitation) => invitation.id}
          loading={isLoading || (isFetching && rows.length === 0)}
          total={data?.total ?? 0}
          page={url.page}
          rowsPerPage={rowsPerPage}
          onPageChange={(page) => url.setFilters({ page })}
          onRowsPerPageChange={(next) => {
            setRowsPerPage(next);
            url.setFilters({ page: 0 });
          }}
          emptyState={emptyState}
        />
      )}

      <ConfirmDialog
        open={Boolean(revoking)}
        tone="destructive"
        title={t("invite.list.revokeTitle", { email: revoking?.email ?? "" })}
        body={t("invite.list.revokeBody")}
        confirmLabel={t("invite.list.revoke")}
        loading={revokingNow}
        onConfirm={revoke}
        onClose={() => setRevoking(null)}
      />

      <InviteStaffDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInvited={(email) =>
          setNotice({
            severity: "success",
            text: t("staff.invited", { email }),
          })
        }
        grantableRoles={scope.grantableRoles}
        districtId={scope.requestDistrictId}
        districtName={districtName}
      />
    </Box>
  );
};

export default InvitationsTab;
